import React, { useEffect, useState } from 'react';
import { useFormSubmit } from '../../../registration/hook/useForm';
import { makeForm, validator } from 'nike-form-js';
import { Map } from 'immutable';
import { empty } from '../../../user-profile/util/validations';
import Input from '../../../forms/Input';
import { TwoFactorSmsIcon } from '../../../../icons/TwoFactorSmsIcon';
import { TwoFactorEmailIcon } from '../../../../icons/TwoFactorEmailIcon';
import ForcedActionCrmType from './ForcedActionCrmType';
import clsx from 'clsx';
import { dispatch, useReframe } from 'nike-re-framejs';
import { startAuthorizeContact, verifyContact } from './handlers';
import Submit from '../../../forms/Submit';
import { useThrottledCallback } from '../../../registration/hook/useThrottledCallback';
import { setForcedActionsCrm } from '../../../user-profile/handlers';
import { getConfig } from '../../../registration/util/config';
import i18n from '../../../../common/i18n';
import useTimeout from '../../../../common/hooks/useTimeout';

const TIMEOUT_PHONE_VERIFICATION = 15 * 60 * 1000;

export const PATH = {
    CODE: ['code'],
    TYPE: ['type']
};

const validators = [[PATH.CODE, empty('validation.code.required')]];

function useForm(initValues, onSubmit) {
    const [form] = useState(() => {
        return makeForm(Map().setIn(PATH.TYPE, initValues.action), { validator: validator(validators) });
    });

    return [form, useFormSubmit(form, onSubmit)];
}

function isPhoneVerification(action) {
    return action === ForcedActionCrmType.PHONE;
}

function TwoFactorIcon({ action }) {
    return isPhoneVerification(action) ? <TwoFactorSmsIcon /> : <TwoFactorEmailIcon />;
}

function resendSms(action, form, onClose) {
    dispatch(startAuthorizeContact(action, form, onClose));
}

function ClientParamChangeVerification({ forcedActionCrm, onClose }) {
    useReframe();
    const { action } = forcedActionCrm;

    function onDone(forcedActionsCrm) {
        dispatch(setForcedActionsCrm(forcedActionsCrm));
        if (onClose) {
            onClose();
        }
        getConfig().refreshUserData();
    }

    function onSubmitHandler(formValues, form) {
        dispatch(verifyContact(formValues, form, onDone));
    }

    const [form, onSubmit] = useForm(forcedActionCrm, onSubmitHandler);
    const [resendSmsThrottled, throttled] = useThrottledCallback(() => resendSms(action, form, onClose), 15000);

    useEffect(() => {
        dispatch(startAuthorizeContact(action, form, onClose));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action]);

    useTimeout(() => onClose && onClose(), isPhoneVerification(action) ? TIMEOUT_PHONE_VERIFICATION : null);

    return (
        <form onSubmit={onSubmit} autoComplete="off">
            <div className="two-factor-ver-box client-param-verification">
                <TwoFactorIcon action={action} />
                <h1 className="two-factor-ver-title">{i18n('nui.two.fa.client.param.change.title.' + action)}</h1>
                <h3 className="two-factor-ver-description">
                    {i18n('nui.two.fa.client.param.change.description.' + action, forcedActionCrm.recipient)}
                </h3>

                <Input
                    form={form}
                    className="client-param-input"
                    path={PATH.CODE}
                    label={i18n('nui.client.param.change.label.code.' + action)}
                    type="text"
                />
                {isPhoneVerification(action) &&
                    (throttled ? <SmsSendRightNow /> : <SmsSendAgain onResendHandler={resendSmsThrottled} />)}
                <Submit
                    form={form}
                    className="two-factor-ver-submit nui-button-180"
                    label={i18n('nui.button.verify')}
                    nuiWrapper={false}
                />
            </div>
        </form>
    );
}

function SmsSendRightNow() {
    return (
        <div className={'flex flex-row items-center py-20'}>
            <span className="blocked">{i18n('nui.registration.change.sms.rightnow')}</span>
        </div>
    );
}

function SmsSendAgain({ onResendHandler }) {
    return (
        <div className={'resend-sms-again'}>
            <a className={clsx('nui-blue-link pointer')} onClick={onResendHandler}>
                {i18n('nui.registration.change.sms')}
            </a>
        </div>
    );
}

export default ClientParamChangeVerification;
